/* istanbul ignore file */
/**
 * Created by gboue on 9/1/22
 * File: PillInput
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useState, useCallback, useEffect, CSSProperties, useRef, MutableRefObject } from 'react'
import { Bold, useOnOutsideClick } from '@panwds/react-ui'
import { LABEL_PILL_INPUT_PLACEHOLDER, ERROR_VALUE } from '../../../iam/constants/LabelConstants'
import Pill from '../pill/Pill'
import isEmail from 'validator/lib/isEmail'

const PillInput: VFC<{
  onChange: Function,
  placeholder: string,
  validate?: Function,
  inputStyle?: CSSProperties,
  validationErrorMessage?: string,
  error: boolean,
  setError: Function
}> = ({ onChange: _onChange, placeholder = LABEL_PILL_INPUT_PLACEHOLDER, validate = isEmail, error, setError, inputStyle, validationErrorMessage = { ERROR_VALUE } }) => {
  const [input, setInput] = useState<string>('')
  const [tags, setTags] = useState<string[]>([])
  const [isKeyReleased, setIsKeyReleased] = useState(false)
  const ref: MutableRefObject<HTMLInputElement | null> = useRef(null)

  useOnOutsideClick(ref, () => {
    const trimmedInput = input.trim() as string

    if (validate && !validate(trimmedInput)) {
      if (!trimmedInput.length) return
      setError(true)
      return
    }
    setError(false)
    setTags(prevState => [...prevState, trimmedInput])
    setInput('')
  })

  useEffect(() => {
    if (_onChange) _onChange(tags)
  }, [_onChange, tags])

  const onChange = useCallback((e) => {
    const { value } = e.target

    if (!value?.length) {
      setError(false)
      setInput('')
    }
    setInput(value)
  }, [setError])

  const onDelete = useCallback((index) => {
    setTags(prevState => prevState.filter((tag, i) => i !== index))
    setInput('')
    setError(false)
  }, [setError])

  const onKeyDown = useCallback((e) => {
    const { key } = e
    const trimmedInput = input.trim() as string

    if (key === 'Enter' && trimmedInput.length && !tags.includes(trimmedInput)) {
      e.preventDefault()
      if (validate && !validate(trimmedInput)) {
        setError(true)
        return
      }
      setError(false)
      setTags(prevState => [...prevState, trimmedInput])
      setInput('')
    }

    if (key === 'Backspace' && !input.length && tags.length && isKeyReleased) {
      const tagsCopy = [...tags]
      const poppedTag = tagsCopy.pop() as string
      e.preventDefault()
      setTags(tagsCopy)
      setInput(poppedTag)
    }

    setIsKeyReleased(false)
  }, [input, isKeyReleased, tags, validate, setError])

  const onKeyUp = useCallback(() => {
    setIsKeyReleased(true)
  }, [])

  return (
    <div className="tw-max-h-100 tw-overflow-y-auto tw-w-full tw-max-w-full tw-p-3 tw-border-solid tw-border-gray-200 dark:tw-border-gray-950 tw-border tw-border-1 tw-border-r-5">
      <div style={inputStyle} className='tw-flex tw-gap-2 tw-flex-wrap'>
        {tags.map((tag, index) => (
          <Pill removable appearance='tertiary' key={index} onDelete={onDelete?.bind(this, index)}>{tag}</Pill>
        ))}
        <input
          ref={ref}
          value={input}
          placeholder={tags?.length > 0 ? undefined : placeholder}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onChange={onChange}
          className='tw-border-none tw-flex-1 focus:tw-outline-none tw-rounded tw-border-0 focus:tw-outline-none tw-placeholder-gray-300 tw-text-default tw-bg-transparent'
        />
      </div>
      {error && <Bold appearance='semibold' addClassName='tw-text-error'>{validationErrorMessage}</Bold>}
    </div>
  )
}

export default PillInput
